// Our own quick look at what the field node's radar sends (Node Control,
// Radar live): the noise floor, echoes refined to a fraction of a bin, the echo
// the radar chose, near-field energy, and an FFT of the beat signal to set
// beside the radar's own spectrum. Pure helpers: no React, network or storage.
//
// Spectra are in dB = 20·log10 of the radar's bin values, assuming linear
// magnitude (unconfirmed). "Bins" are the radar's 256 range cells; a bin is
// step_mm apart and the reported distance is bin × step_mm − cal_mm.
import { finite, pollValue } from "utils/radarDiagnostics";

export const ASSUMED_STEP_MM = 30; // c/2B for the radar's 5 GHz sweep
export const NEAR_FIELD_BINS = 3; // leakage, the lens and anything on it
export const ECHO_SNR_DB = 8; // our threshold: this far over the noise floor
export const PROMINENCE_DB = 3;
export const NEAR_PROMINENCE_DB = 6; // within the leakage skirt
export const SKIRT_BINS = 8;
export const WIDTH_DROP_DB = 6;
export const PICK_RADIUS_BINS = 2;
export const ADC_FULL_SCALE = 32767;

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const at = (db, i) => (db && i >= 0 && i < db.length ? num(db[i]) : null);

// Linear-interpolated quantile (0-1) of the finite values, or null.
export function quantile(values, q) {
  const v = [];
  for (const x of values || []) {
    const n = num(x);
    if (n !== null) v.push(n);
  }
  if (!v.length) return null;
  v.sort((a, b) => a - b);
  const pos = (v.length - 1) * Math.min(1, Math.max(0, q));
  const lo = Math.floor(pos);
  const hi = Math.min(v.length - 1, lo + 1);
  return v[lo] + (v[hi] - v[lo]) * (pos - lo);
}

// The noise floor: the median of the bins beyond the near field, so up to
// half of them can hold echoes without moving it.
export function noiseFloor(db, { skip = NEAR_FIELD_BINS, q = 0.5 } = {}) {
  if (!db || typeof db.length !== "number") return null;
  return quantile(Array.prototype.slice.call(db, skip), q);
}

// The vertex of the parabola through a peak bin (b) and its neighbours (a, c),
// on dB values (a Gaussian fit in the log domain): the offset from the peak
// bin, in bins (-0.5 to 0.5), and the level there.
export function parabolicPeak(a, b, c) {
  if (num(a) === null || num(b) === null || num(c) === null)
    return { offset: 0, level: num(b) };
  const den = a - 2 * b + c;
  if (den >= 0) return { offset: 0, level: b };
  const offset = Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / den));
  return { offset, level: b - 0.25 * (a - c) * offset };
}

const isPeak = (db, i) => {
  const a = at(db, i - 1);
  const b = at(db, i);
  const c = at(db, i + 1);
  return a !== null && b !== null && c !== null && b > a && b >= c;
};

// Height over the higher of the two lowest points between the peak and the
// next higher bin (or the end) on either side.
function prominence(db, i) {
  const top = at(db, i);
  const side = (dir) => {
    let low = top;
    for (let j = i + dir; j >= 0 && j < db.length; j += dir) {
      const v = at(db, j);
      if (v === null) continue;
      if (v > top) break;
      if (v < low) low = v;
    }
    return low;
  };
  return top - Math.max(side(-1), side(1));
}

// Where the curve first falls to `level` walking from bin i in `dir`, in
// fractional bins (linear between bins); null when it rises again first (a
// neighbouring echo) or runs out of bins. Bin 0 is DC, which the radar (and
// our FFT) removes, so it does not count as a crossing; nor does a bin i that
// is not above the level (a lopsided parabola put the top over 6 dB above it,
// as next to bin 0).
function crossing(db, i, level, dir) {
  let prev = at(db, i);
  if (prev === null || prev <= level) return null;
  for (let j = i + dir; j >= 1 && j < db.length; j += dir) {
    const v = at(db, j);
    if (v === null) return null;
    if (v <= level) return j - dir + (dir * (prev - level)) / (prev - v);
    if (v > prev) return null;
    prev = v;
  }
  return null;
}

// The -6 dB width of the peak at bin i: where the curve crosses `drop` dB
// below `top` on each side, in bins. Null unless both sides are found (the
// near field, the spectrum's ends, a neighbouring echo), so never negative.
export function peakWidth(
  db,
  i,
  { top = at(db, i), drop = WIDTH_DROP_DB } = {},
) {
  if (num(top) === null || at(db, i) === null) return null;
  const left = crossing(db, i, top - drop, -1);
  const right = crossing(db, i, top - drop, 1);
  return left === null || right === null || !(right > left)
    ? null
    : { left, right, width: right - left };
}

function describePeak(db, i, floor) {
  const { offset, level } = parabolicPeak(
    at(db, i - 1),
    at(db, i),
    at(db, i + 1),
  );
  return {
    index: i,
    bin: i + offset,
    level,
    snr: floor === null ? null : level - floor,
    prominence: prominence(db, i),
    width: peakWidth(db, i, { top: level }),
  };
}

// Echoes: local maxima beyond bin 0 with at least 3 dB prominence (6 dB in the
// first 8 bins, the leakage skirt) and above a threshold - one value per bin
// (the radar's own curve), else `minSnr` over the noise floor. Strongest first.
export function findPeaks(
  db,
  { floor = noiseFloor(db), minSnr = ECHO_SNR_DB, threshold = null } = {},
) {
  if (!db || !db.length) return [];
  const out = [];
  for (let i = 1; i < db.length - 1; i++) {
    if (!isPeak(db, i)) continue;
    if (
      prominence(db, i) < (i < SKIRT_BINS ? NEAR_PROMINENCE_DB : PROMINENCE_DB)
    )
      continue;
    const limit = threshold
      ? at(threshold, i)
      : floor === null
        ? null
        : floor + minSnr;
    if (threshold && limit === null) continue;
    if (limit !== null && at(db, i) <= limit) continue;
    out.push(describePeak(db, i, floor));
  }
  return out.sort((a, b) => b.level - a.level);
}

// The echo at a given bin (the radar's pick): the local maximum nearest to it
// within ±radius bins, described like any other peak; null when there is none.
export function echoNear(
  db,
  bin,
  { floor = noiseFloor(db), radius = PICK_RADIUS_BINS } = {},
) {
  if (!db || !db.length || num(bin) === null) return null;
  let best = null;
  const from = Math.max(1, Math.floor(bin - radius));
  const to = Math.min(db.length - 2, Math.ceil(bin + radius));
  for (let i = from; i <= to; i++)
    if (
      isPeak(db, i) &&
      (best === null || Math.abs(i - bin) < Math.abs(best - bin))
    )
      best = i;
  return best === null ? null : describePeak(db, best, floor);
}

// Mean power of the first bins (leakage, the lens and anything on it) on the
// spectrum's dB scale and over the noise floor. A film or drops on the lens
// raise it; so does anything very close to the radar.
export function nearFieldEnergy(
  db,
  { bins = NEAR_FIELD_BINS, floor = noiseFloor(db) } = {},
) {
  let sum = 0;
  let n = 0;
  for (let i = 0; i < Math.min(bins, db?.length || 0); i++) {
    const v = at(db, i);
    if (v === null) continue;
    sum += 10 ** (v / 10);
    n += 1;
  }
  if (!n) return null;
  const level = 10 * Math.log10(sum / n);
  return { level, overFloor: floor === null ? null : level - floor, bins: n };
}

// The radar's reported distance when it is one: not missing, not a 1/2/3 mm
// sentinel for weak echo, blind zone or out of range, above 0 and within
// `reachMm`, the spectrum's reach (a bad decode can report 0 or far beyond).
export function pickDistance(value, reachMm = Infinity) {
  const v = finite(value);
  return v !== null && v > 0 && v <= reachMm && !pollValue(v).sentinel
    ? v
    : null;
}

// How far a spectrum reaches: its last bin's reported distance.
export const spectrumReachMm = (step, cal, bins = 256) =>
  (bins - 1) * (finite(step) || ASSUMED_STEP_MM) - (finite(cal) ?? 0);

// The radar's peak bin when it is one of the spectrum's bins (a bad decode
// can report 25882), else null.
export function validPeakBin(value, bins = 256) {
  const v = finite(value);
  return v !== null && Number.isInteger(v) && v >= 0 && v < bins ? v : null;
}

// Everything we compute from one capture: { db, thr_db?, air_mm, peak_bin,
// step_mm, cal_mm } -> the noise floor, our five strongest echoes, the echo
// the radar chose (nearest its reported distance, else its peak bin; neither
// when they are not valid) with its SNR and -6 dB width, near-field energy,
// and how many echoes clear the threshold (the radar's own curve when the
// capture has one, else ours).
export function analyzeSpectrum(capture) {
  const db = capture?.db;
  if (
    !db ||
    !db.length ||
    !Array.prototype.some.call(db, (v) => num(v) !== null)
  )
    return null;
  const step = finite(capture.step_mm) || ASSUMED_STEP_MM;
  const cal = finite(capture.cal_mm) ?? 0;
  const toMm = (bin) => bin * step - cal;
  const floor = noiseFloor(db);
  const peaks = findPeaks(db, { floor });
  const thr =
    Array.isArray(capture.thr_db) && capture.thr_db.some((v) => num(v) !== null)
      ? capture.thr_db
      : null;
  const air = pickDistance(
    capture.air_mm,
    spectrumReachMm(step, cal, db.length),
  );
  const peakBin = validPeakBin(capture.peak_bin, db.length);
  const pick =
    air !== null
      ? { source: "distance", bin: (air + cal) / step, mm: air }
      : peakBin !== null
        ? { source: "peak bin", bin: peakBin, mm: toMm(peakBin) }
        : null;
  const withMm = (p) =>
    p && {
      ...p,
      mm: toMm(p.bin),
      widthMm: p.width ? p.width.width * step : null,
      leftMm: p.width ? toMm(p.width.left) : null,
      rightMm: p.width ? toMm(p.width.right) : null,
    };
  return {
    step,
    cal,
    floor,
    peaks: peaks.slice(0, 5).map(withMm),
    peakCount: peaks.length,
    pick,
    chosen: pick ? withMm(echoNear(db, pick.bin, { floor })) : null,
    nearField: nearFieldEnergy(db, { floor }),
    echoes: thr
      ? { count: findPeaks(db, { floor, threshold: thr }).length, by: "radar" }
      : { count: peaks.length, by: "ours" },
  };
}

// In-place iterative radix-2 FFT of re/im (equal power-of-two lengths).
export function fft(re, im) {
  const n = re.length;
  if (n < 2 || n & (n - 1) || im.length !== n)
    throw new Error("FFT length must be a power of two");
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    for (let k = 0; k < half; k++) {
      const angle = (-2 * Math.PI * k) / len;
      const wr = Math.cos(angle);
      const wi = Math.sin(angle);
      for (let i = k; i < n; i += len) {
        const j = i + half;
        const xr = re[j] * wr - im[j] * wi;
        const xi = re[j] * wi + im[j] * wr;
        re[j] = re[i] - xr;
        im[j] = im[i] - xi;
        re[i] += xr;
        im[i] += xi;
      }
    }
  }
}

// Our FFT of the beat signal, or of samples [from, to) of it: the mean (the
// ADC's DC offset) removed, a Hann window (or none, "rect": node 1's radar
// computes its spectrum as |rfft(adc - mean)| without a window, so ours
// unpadded matches it, r = 0.998 in dB), zero-padded `pad` times over the
// whole signal's length, magnitude in dB for the first half. With 512
// samples and pad 8 that is 2,048 values, 8 per radar bin (radar bin =
// index / pad), also for a part of the signal, which resolves less. Scaled
// so a sine of amplitude A at the centre of a bin reads 20·log10(A).
export function beatSpectrum(
  samples,
  { pad = 8, window = "hann", from = 0, to = null } = {},
) {
  const x = Array.from(samples || [], (v) => num(Number(v)) ?? 0);
  const n = x.length;
  if (n < 8) return null;
  const start = Math.max(0, Math.min(n, Math.floor(from) || 0));
  const end = Math.max(start, Math.min(n, to === null ? n : Math.floor(to)));
  const m = end - start;
  if (m < 8) return null;
  let size = 1;
  while (size < n * pad) size <<= 1;
  let mean = 0;
  for (let i = start; i < end; i++) mean += x[i];
  mean /= m;
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  let gain = 0;
  for (let i = 0; i < m; i++) {
    const w =
      window === "rect" ? 1 : 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / m);
    re[i] = (x[start + i] - mean) * w;
    gain += w;
  }
  fft(re, im);
  const db = new Float64Array(size / 2);
  for (let k = 0; k < db.length; k++) {
    const amplitude = (2 * Math.hypot(re[k], im[k])) / gain;
    db[k] = amplitude > 0 ? 20 * Math.log10(amplitude) : -Infinity;
  }
  return { db, pad: size / n, mean, samples: m, from: start, to: end, window };
}

// The beat signal at a glance: range, mean (DC offset), RMS about the mean,
// and samples at the int16 limits (clipping).
export function adcStats(samples) {
  const v = Array.from(samples || [], Number).filter(Number.isFinite);
  if (!v.length) return null;
  const mean = v.reduce((a, b) => a + b, 0) / v.length;
  return {
    n: v.length,
    min: Math.min(...v),
    max: Math.max(...v),
    mean,
    rms: Math.sqrt(v.reduce((a, b) => a + (b - mean) ** 2, 0) / v.length),
    clipped: v.filter((s) => s >= ADC_FULL_SCALE || s <= -ADC_FULL_SCALE - 1)
      .length,
  };
}

// The strongest echo beyond the near field: its position (fractional bins),
// level, -6 dB width (bins) and how far the noise floor lies below it.
function strongestBeyond(db, from) {
  let best = null;
  for (let i = Math.max(1, from); i < db.length - 1; i++)
    if (isPeak(db, i) && (best === null || db[i] > db[best])) best = i;
  if (best === null) return null;
  const floor = noiseFloor(db, { skip: from });
  const p = describePeak(db, best, floor);
  return {
    bin: p.bin,
    level: p.level,
    width: p.width?.width ?? null,
    floorBelow: p.snr,
  };
}

// The radar's spectrum beside ours, in radar bins: each one's strongest echo
// beyond the near field, its -6 dB width and its floor below the peak, and
// how far apart the two positions are.
export function compareSpectra(radarDb, ours) {
  const radar = radarDb?.length
    ? strongestBeyond(radarDb, NEAR_FIELD_BINS)
    : null;
  let mine = null;
  if (ours?.db?.length) {
    const found = strongestBeyond(ours.db, NEAR_FIELD_BINS * ours.pad);
    mine = found && {
      ...found,
      bin: found.bin / ours.pad,
      width: found.width === null ? null : found.width / ours.pad,
    };
  }
  return {
    radar,
    ours: mine,
    delta: radar && mine ? mine.bin - radar.bin : null,
  };
}

// The strongest finite value from index `from` on, or null.
const strongest = (values, from) => {
  let top = null;
  for (let i = from; i < (values?.length || 0); i++) {
    const v = values[i];
    if (Number.isFinite(v) && (top === null || v > top)) top = v;
  }
  return top;
};

// Rows on reported distances: ours at every padded bin, the radar's at every
// `pad`-th row; each scaled to 0 dB at its own strongest echo beyond the
// near field, because the radar's scale is unknown.
// `rect`, our FFT without a window or padding (the radar's own way), joins
// the radar's rows the same way.
export function comparisonRows(
  radarDb,
  ours,
  step,
  cal,
  [lo, hi],
  rect = null,
) {
  if (!ours) return [];
  const refOurs = strongest(ours.db, NEAR_FIELD_BINS * ours.pad);
  const refRadar = radarDb ? strongest(radarDb, NEAR_FIELD_BINS) : null;
  const refRect = rect ? strongest(rect.db, NEAR_FIELD_BINS * rect.pad) : null;
  const rows = [];
  for (let k = 0; k < ours.db.length; k++) {
    const d = (k / ours.pad) * step - cal;
    if (d < lo || d > hi) continue;
    const mine = ours.db[k];
    const bin = k / ours.pad;
    const theirs =
      Number.isInteger(bin) && refRadar !== null ? finite(radarDb[bin]) : null;
    const plain =
      Number.isInteger(bin) && refRect !== null
        ? rect.db[bin * rect.pad]
        : null;
    rows.push({
      d,
      ours: Number.isFinite(mine) && refOurs !== null ? mine - refOurs : null,
      radar: theirs === null ? null : theirs - refRadar,
      ...(rect
        ? { rect: Number.isFinite(plain) ? plain - refRect : null }
        : {}),
    });
  }
  return rows;
}

// The echo nearest `nearBin` (radar bins) in our zero-padded FFT of the beat
// signal, to a fraction of a radar bin: finer than a parabola on the radar's
// unwindowed spectrum, whose main lobe nearby echoes distort (node 1's water:
// 25.62 bins, 789 mm, from the beat signal against 816 mm from the
// spectrum). -> {bin, level}, or null.
export function finerEcho(fft, nearBin, { radius = PICK_RADIUS_BINS } = {}) {
  if (!fft?.db?.length || num(nearBin) === null) return null;
  const db = Array.from(fft.db, (v) => (Number.isFinite(v) ? v : null));
  const p = echoNear(db, nearBin * fft.pad, {
    radius: radius * fft.pad,
    floor: noiseFloor(db, { skip: NEAR_FIELD_BINS * fft.pad }),
  });
  return p ? { bin: p.bin / fft.pad, level: p.level } : null;
}

// Clipping in the beat signal, measured as the backend does (radar.py
// adc_clipping): node 1's receiver saturates in its analog stage on a strong
// close echo (flat tops near +7,160 and -7,110 counts, well inside int16).
// The rails are the capture's own maximum (hi) and minimum (lo); a sample is
// at a rail within 6% of that rail's distance from the mean (3% of the span
// for a centred signal). A run at a rail counts when it is at least 3 samples
// long and longer than a sine of the capture's own period could stay there,
// floor(0.111 × period) + 2 samples, the period being twice the mean spacing
// of the signal's crossings of its mean (with 1% of the span as hysteresis):
// the rounded peaks of a sine never count. pct is the share of all samples in
// runs that count (6.6% on node 1), first and last the first and last of
// them. A capture spanning fewer than 256 counts has no clipping.
export const CLIP_BAND = 0.03;
export const CLIP_RUN = 3;
export const CLIP_MIN_SPAN = 256;
export const CLIP_HYSTERESIS = 0.01;
const SINE_DWELL = Math.acos(1 - 2 * CLIP_BAND) / Math.PI;
const runsOf = (flags) => {
  const found = [];
  let start = null;
  flags.forEach((flag, i) => {
    if (flag && start === null) start = i;
    else if (!flag && start !== null) {
      found.push([start, i - start]);
      start = null;
    }
  });
  if (start !== null) found.push([start, flags.length - start]);
  return found;
};
export function clipping(samples) {
  const v = Array.from(samples || [], Number).filter(Number.isFinite);
  const n = v.length;
  if (!n) return null;
  const hi = Math.max(...v);
  const lo = Math.min(...v);
  const found = { clipped: false, pct: 0, first: null, last: null, hi, lo };
  if (hi - lo < CLIP_MIN_SPAN) return found;
  const mean = v.reduce((a, b) => a + b, 0) / n;
  const band = CLIP_HYSTERESIS * (hi - lo);
  const crossings = [];
  let side = null;
  v.forEach((x, i) => {
    const now = x > mean + band ? true : x < mean - band ? false : side;
    if (side !== null && now !== side) crossings.push(i);
    side = now;
  });
  const period =
    crossings.length >= 2
      ? (2 * (crossings[crossings.length - 1] - crossings[0])) /
        (crossings.length - 1)
      : 2 * n;
  const need = Math.max(CLIP_RUN, Math.floor(SINE_DWELL * period) + 2);
  const at = [];
  for (const flags of [
    v.map((x) => x >= hi - 2 * CLIP_BAND * (hi - mean)),
    v.map((x) => x <= lo + 2 * CLIP_BAND * (mean - lo)),
  ])
    for (const [start, length] of runsOf(flags))
      if (length >= need)
        for (let i = start; i < start + length; i++) at.push(i);
  if (!at.length) return found;
  at.sort((a, b) => a - b);
  return {
    ...found,
    clipped: true,
    pct: Math.round((1000 * at.length) / n) / 10,
    first: at[0],
    last: at[at.length - 1],
  };
}
// A capture's clipping: the server's (radar-captures/<id>/ clip {pct, first,
// last, hi, lo}) when it has one, else measured here from the samples.
export function clipFrom(detail, samples = null) {
  const c = detail?.clip;
  if (c && typeof c === "object" && finite(c.pct) !== null) {
    const first = finite(c.first);
    const last = finite(c.last);
    return {
      clipped: c.pct > 0 && first !== null && last !== null,
      pct: c.pct,
      first,
      last,
      hi: finite(c.hi),
      lo: finite(c.lo),
    };
  }
  return samples ? clipping(samples) : null;
}
// "clipped: 6.6% of samples, 8-105"
export const clipText = (clip) =>
  `clipped: ${clip.pct.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% of samples, ${clip.first}-${clip.last}`;

// With a clipping receiver, echoes at about 2, 3 or 4 times the strongest
// echo's range (within ±1.5 bins) may be its harmonics rather than echoes
// (node 1: 25.62, 51.00, 75.38 and 102.38 bins). The strongest echo beyond
// the near field is the fundamental, unless `base` (a finer bin, from the
// beat signal) is given. -> the multiple for each peak, or null.
export function harmonicOf(
  peaks,
  { base = null, tolerance = 1.5, orders = [2, 3, 4] } = {},
) {
  const strongest = (peaks || [])
    .filter((p) => p.bin >= NEAR_FIELD_BINS)
    .reduce((a, b) => (!a || b.level > a.level ? b : a), null);
  const f = num(base) ?? strongest?.bin ?? null;
  return (peaks || []).map((p) => {
    if (f === null || p === strongest || p.bin < NEAR_FIELD_BINS) return null;
    return orders.find((k) => Math.abs(p.bin - k * f) <= tolerance) ?? null;
  });
}
